import React, { useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, Upload, DatabaseBackup, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function AdminMigration() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [includeContent, setIncludeContent] = useState(true);
  const [report, setReport] = useState(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState(""); // "uploading" | "processing" | "done" | "error"
  const fileRef = useRef(null);

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/config/export", {
        params: { include_content: includeContent },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `natalmatico-config-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Configuração exportada");
    } catch (e) {
      toast.error("Falha ao exportar");
    } finally {
      setExporting(false);
    }
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!window.confirm(
      "Importar esta configuração? As definições, textos, botões e diplomas serão SUBSTITUÍDOS por este ficheiro. Os quizzes/perguntas são atualizados por id. Dados de utilizadores (participações, contas, pagamentos) NÃO são afetados."
    )) return;
    setImporting(true);
    setReport(null);
    setUploadPct(0);
    setPhase("uploading");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/config/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadPct(pct);
            if (pct >= 100) setPhase("processing");
          }
        },
      });
      setReport(data.report);
      setPhase("done");
      toast.success("Configuração importada");
    } catch (e) {
      setPhase("error");
      toast.error("Falha ao importar: " + (e?.response?.data?.detail || "erro"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="admin-migration-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] flex items-center gap-2">
          <DatabaseBackup size={26} className="text-[#005F73]" /> Migração de Configuração
        </h1>
        <p className="text-sm text-[#0B1D3A]/60 mt-1">
          Transfere a configuração do site (definições, textos, botões, diplomas, inscrição) e os quizzes/perguntas — incluindo as imagens/música — entre ambientes. Útil para aplicar o preview na produção.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-[#FFC107]/40 bg-[#FFF8E1] px-5 py-4">
        <AlertTriangle size={20} className="text-[#B7791F] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0B1D3A]/75">
          Faça <b>Exportar</b> neste ambiente (preview) para descarregar o ficheiro. Depois, no ambiente de <b>produção</b>, faça <b>Importar</b> e carregue esse ficheiro. A importação <b>substitui</b> a configuração visual e <b>nunca</b> toca em participações, credenciais, contas ou pagamentos.
        </p>
      </div>

      {/* Export */}
      <div className="rounded-2xl bg-white p-6 border border-[#0B1D3A]/5 space-y-4">
        <h2 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><Download size={18} /> Exportar configuração</h2>
        <label className="flex items-center gap-2 text-sm text-[#0B1D3A]/80">
          <input type="checkbox" checked={includeContent} onChange={(e) => setIncludeContent(e.target.checked)} data-testid="export-include-content" />
          Incluir quizzes e perguntas
        </label>
        <button onClick={doExport} disabled={exporting} data-testid="export-config-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#005F73] text-white font-bold disabled:opacity-60">
          <Download size={16} /> {exporting ? "A exportar..." : "Exportar configuração"}
        </button>
      </div>

      {/* Import */}
      <div className="rounded-2xl bg-white p-6 border border-[#0B1D3A]/5 space-y-4">
        <h2 className="font-outfit font-bold text-[#0B1D3A] flex items-center gap-2"><Upload size={18} /> Importar configuração</h2>
        <p className="text-xs text-[#0B1D3A]/55">Selecione o ficheiro JSON exportado de outro ambiente.</p>
        <input ref={fileRef} type="file" accept="application/json,.json" onChange={doImport} disabled={importing} data-testid="import-config-input" className="text-sm" />

        {importing && (
          <div className="space-y-2" data-testid="import-progress">
            <div className="flex items-center justify-between text-sm font-semibold text-[#0B1D3A]">
              <span>
                {phase === "uploading" && "A enviar ficheiro..."}
                {phase === "processing" && "A processar no servidor (a restaurar imagens e configuração)..."}
              </span>
              {phase === "uploading" && <span data-testid="import-upload-pct">{uploadPct}%</span>}
            </div>
            <div className="h-2.5 rounded-full bg-[#0B1D3A]/10 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-200 ${phase === "processing" ? "animate-pulse" : ""}`}
                style={{ width: `${phase === "processing" ? 100 : uploadPct}%`, background: "#005F73" }}
                data-testid="import-progress-bar"
              />
            </div>
            {phase === "processing" && (
              <p className="text-xs text-[#0B1D3A]/55">O envio terminou. O servidor está a guardar os ficheiros e a aplicar a configuração — aguarde, não feche a página.</p>
            )}
          </div>
        )}

        {report && (
          <div className="rounded-xl border border-[#2D9C5A]/30 bg-[#2D9C5A]/10 p-4 text-sm" data-testid="import-report">
            <p className="font-bold text-[#2D9C5A] flex items-center gap-2 mb-2"><CheckCircle2 size={16} /> Importação concluída</p>
            <p className="text-[#0B1D3A]/75">Ficheiros restaurados: <b>{report.files}</b>{report.files_failed ? ` (falharam: ${report.files_failed})` : ""}</p>
            <ul className="mt-1 text-[#0B1D3A]/70 list-disc pl-5">
              {Object.entries(report.collections || {}).map(([k, v]) => (
                <li key={k}>{k}: {v} registos</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
